import { ref, computed, type Ref } from "vue";
import { auth, functions } from "@/firebase";
import { onAuthStateChanged, type User } from "firebase/auth";
import { httpsCallable } from "firebase/functions";

type ReadyHandle = {
  value: Ref<boolean>;
  wait: () => Promise<void>;
};

const currentUserRef = ref<User | null>(auth.currentUser);
const isSuperAdminRef = ref<boolean>(false);

// Simple ready gate with reset capability
let resolveReady: (() => void) | null = null;
let readyPromise: Promise<void> = new Promise((resolve) => {
  resolveReady = resolve;
});
const readyValue = ref<boolean>(false);
const ready: ReadyHandle = {
  value: readyValue,
  wait: () => readyPromise,
};

function setReady(next: boolean) {
  if (next === readyValue.value) return;
  readyValue.value = next;
  if (next && resolveReady) {
    resolveReady();
  }
  if (!next) {
    // create a new deferred promise for the next ready cycle
    readyPromise = new Promise((resolve) => {
      resolveReady = resolve;
    });
  }
}

let syncInFlight: Promise<void> | null = null;

async function syncClaims(): Promise<void> {
  if (syncInFlight) return syncInFlight;

  const run = async () => {
    const user = auth.currentUser;
    if (!user) {
      isSuperAdminRef.value = false;
      return;
    }
    try {
      const sync = httpsCallable(functions, "syncCustomClaimsOnLogin");
      await sync();
      await user.getIdToken(true);
      const result = await user.getIdTokenResult();
      console.log(
        "useSuperAdminClaims: token claims after sync",
        result.claims
      );
      isSuperAdminRef.value = result.claims?.["superAdmin"] === true;
    } catch (err) {
      console.error("useSuperAdminClaims: syncCustomClaimsOnLogin failed", err);
      // Fail soft – assume not super admin on error
      isSuperAdminRef.value = false;
    }
  };

  syncInFlight = run()
    .catch(() => {})
    .finally(() => {
      syncInFlight = null;
    });
  return syncInFlight;
}

// Initialize listeners once at module load
onAuthStateChanged(auth, async (user) => {
  currentUserRef.value = user;
  setReady(false);
  try {
    if (!user) {
      isSuperAdminRef.value = false;
      return;
    }
    // Read existing claims from the current ID token (no forced refresh)
    const tokenResult = await user.getIdTokenResult();
    const hasSuperAdminClaim = tokenResult.claims?.["superAdmin"] === true;
    isSuperAdminRef.value = hasSuperAdminClaim;

    // Only sync if user doesn't already have super admin claim
    // This prevents unnecessary calls while ensuring claims are updated when needed
    if (!hasSuperAdminClaim) {
      await syncClaims();
    }
  } catch (err) {
    console.error(
      "useSuperAdminClaims: failed to read claims on auth state change",
      err
    );
    isSuperAdminRef.value = false;
  } finally {
    setReady(true);
  }
});

export function useSuperAdminClaims() {
  const user = computed(() => currentUserRef.value);
  const isSuperAdmin = computed(() => isSuperAdminRef.value);
  return {
    user,
    isSuperAdmin,
    ready,
    resync: async () => {
      setReady(false);
      await syncClaims();
      setReady(true);
    },
  };
}
